import React from "react";
import { PHASE_3_PALETTE } from "./palette";

// Phase 3 (B1): donut chart mirroring the rounded chart on PDF page 1 (Modules
// donut) and page 5 (Cost summary donut). Pure SVG so html-to-image captures it
// deterministically with no canvas/timing dependencies.
//
// Props
//   data        Array<{ label: string, value: number }>
//   width       svg width  (default 220, fits side-by-side donut blocks in A4)
//   title       optional string rendered above the donut (e.g. "2024")
//   showLabels  bool  draws label + percentage outside each slice (default true)
//   palette     string[]  optional colour override; defaults to PHASE_3_PALETTE.donut
//   innerRatio  number 0..1  fraction of outer radius reserved for the hole (default 0.6)
// Pushes labels apart vertically on each side so adjacent small slices don't
// produce overlapping/scrambled text (e.g. Etang B's Vente / Dépôt / Restauration).
// Mutates `labelY` on each slice that has `showLabel`. ~13 px = 10 px font line height
// (bumped after M1.2 readability fix that raised donut label font to 10).
const MIN_LABEL_GAP_PX = 13;
const deCollideLabels = (slices) => {
  const left = slices.filter((s) => s.showLabel && s.labelAnchor === "end").sort((a, b) => a.labelY - b.labelY);
  const right = slices.filter((s) => s.showLabel && s.labelAnchor === "start").sort((a, b) => a.labelY - b.labelY);
  for (const side of [left, right]) {
    for (let i = 1; i < side.length; i++) {
      if (side[i].labelY - side[i - 1].labelY < MIN_LABEL_GAP_PX) {
        side[i].labelY = side[i - 1].labelY + MIN_LABEL_GAP_PX;
      }
    }
  }
};

const DonutChart = ({
  data,
  width = 220,
  title,
  showLabels = true,
  palette = PHASE_3_PALETTE.donut,
  innerRatio = 0.6,
}) => {
  if (!Array.isArray(data) || data.length === 0) return null;

  const total = data.reduce((sum, d) => sum + Math.max(0, Number(d.value) || 0), 0);
  if (total === 0) return null;

  const titleHeight = title ? 22 : 0;
  // M1.4: bumped to 95 — at 72 the trailing "%" on labels like "Chauffage 49.3%"
  // was getting clipped on the right edge of the SVG viewBox, rendering as
  // "Chauffage 49" in the Primerose Résumé des coûts donut. 95px gives enough
  // room for the longest label form (e.g. "Climatisation 31.2%") at 10pt.
  const labelMargin = showLabels ? 95 : 8;
  const size = width;
  const height = size + titleHeight;
  const cx = size / 2;
  const cy = titleHeight + size / 2;
  const radius = size / 2 - labelMargin;
  const innerRadius = radius * innerRatio;

  // Identify positive-value entries (zero ones are dropped so the math stays clean).
  const positiveEntries = data
    .map((d, i) => ({ ...d, palettteIndex: i, value: Math.max(0, Number(d.value) || 0) }))
    .filter((d) => d.value > 0);

  // Edge case — single category at 100%. SVG arcs cannot draw a closed loop where
  // start == end, so a literal 1-slice donut would render nothing. Fall back to a
  // ring stroked from a circle, which produces the same visual.
  const singleFull = positiveEntries.length === 1 && positiveEntries[0].value === total;

  // Compute slice arcs.
  let cursorAngle = -Math.PI / 2; // start at 12 o'clock
  const slices = [];
  for (let i = 0; i < positiveEntries.length; i++) {
    const entry = positiveEntries[i];
    const v = entry.value;
    if (singleFull) {
      // Path is unused for the single-full case; we still emit the slice metadata
      // so the label renders.
      slices.push({
        key: entry.label + "_" + entry.palettteIndex,
        d: null,
        fill: palette[entry.palettteIndex % palette.length],
        label: entry.label,
        labelX: cx,
        labelY: cy - radius - 10,
        labelAnchor: "middle",
        labelPct: "100.0%",
      });
      break;
    }
    const fraction = v / total;
    const startAngle = cursorAngle;
    const endAngle = cursorAngle + fraction * 2 * Math.PI;
    cursorAngle = endAngle;

    const x0 = cx + radius * Math.cos(startAngle);
    const y0 = cy + radius * Math.sin(startAngle);
    const x1 = cx + radius * Math.cos(endAngle);
    const y1 = cy + radius * Math.sin(endAngle);
    const xi0 = cx + innerRadius * Math.cos(endAngle);
    const yi0 = cy + innerRadius * Math.sin(endAngle);
    const xi1 = cx + innerRadius * Math.cos(startAngle);
    const yi1 = cy + innerRadius * Math.sin(startAngle);

    const largeArc = endAngle - startAngle > Math.PI ? 1 : 0;

    const d = [
      `M ${x0} ${y0}`,
      `A ${radius} ${radius} 0 ${largeArc} 1 ${x1} ${y1}`,
      `L ${xi0} ${yi0}`,
      `A ${innerRadius} ${innerRadius} 0 ${largeArc} 0 ${xi1} ${yi1}`,
      "Z",
    ].join(" ");

    // Label position (middle of slice, just outside the outer radius). Tiny
    // adjacent slices on big donuts (e.g. Etang B's Vente 5.2 / Dépôt 5.6 /
    // Restauration 2.5 / Vitrine 0.0 next to Bureau 86.6) tend to collide. We
    // suppress labels on slices below 1.0 % so the surviving labels stay legible.
    // M1.4: dropped threshold from 1.5 → 1.0 so Primerose's Climatisation 1.3 %
    // slice keeps its label (was hidden in the previous export). The collision
    // helper (deCollideLabels above) already handles the tighter spacing.
    const midAngle = (startAngle + endAngle) / 2;
    const labelR = radius + 14;
    const labelX = cx + labelR * Math.cos(midAngle);
    const labelY = cy + labelR * Math.sin(midAngle);
    const labelAnchor = Math.cos(midAngle) >= 0 ? "start" : "end";
    const labelPct = (fraction * 100).toFixed(1) + "%";
    const showLabel = fraction >= 0.01; // ≥ 1.0 %

    slices.push({
      key: data[i].label + "_" + i,
      d,
      fill: palette[i % palette.length],
      label: data[i].label,
      labelX,
      labelY,
      labelAnchor,
      labelPct,
      showLabel,
    });
  }

  // Collision-avoidance: keep adjacent visible labels at least 11 px apart on
  // each side of the donut so small slices (Vente / Dépôt / Restauration on
  // Etang B) stop overlapping. Helper extracted so the main function stays
  // simple — see deCollideLabels below.
  deCollideLabels(slices);

  return (
    // M1.1: dropped the hardcoded fontFamily on the SVG so labels inherit
    // PP Neue Montreal from the .repartition-page wrapper. With the previous
    // "system-ui, sans-serif" inline style the donut text rendered in a
    // different face than the rest of the report — the client noticed it as
    // "donut chart has some texte". When rendered outside the Répartition
    // report (e.g. KeyDiagramView later), the parent's font cascades instead.
    <svg width={width} height={height} viewBox={`0 0 ${width} ${height}`}>
      {title && (
        <text x={cx} y={14} textAnchor="middle" fontSize="11" fill={PHASE_3_PALETTE.text}>
          {title}
        </text>
      )}
      {/* Single-slice 100% donut: draw the ring as a stroked circle since an SVG
          arc cannot start and end at the same point. */}
      {singleFull && (
        <circle
          cx={cx}
          cy={cy}
          r={(radius + innerRadius) / 2}
          fill="none"
          stroke={slices[0].fill}
          strokeWidth={radius - innerRadius}
        />
      )}
      {!singleFull && slices.map((s) => (
        <g key={s.key}>
          <path d={s.d} fill={s.fill} stroke="#fff" strokeWidth="1" />
          {showLabels && s.showLabel && (
            <text
              x={s.labelX}
              y={s.labelY}
              textAnchor={s.labelAnchor}
              dominantBaseline="middle"
              fontSize="10"
              fill={PHASE_3_PALETTE.text}
            >
              {s.label} {s.labelPct}
            </text>
          )}
        </g>
      ))}
      {singleFull && showLabels && (
        <text
          x={slices[0].labelX}
          y={slices[0].labelY}
          textAnchor="middle"
          dominantBaseline="middle"
          fontSize="9"
          fill={PHASE_3_PALETTE.text}
        >
          {slices[0].label} {slices[0].labelPct}
        </text>
      )}
    </svg>
  );
};

export default DonutChart;
